import { RawButton } from '@/components/Button'
import styles from './SavedFilterPeek.module.css'

export interface SavedFilterPeekFilter {
  key: string
  title: string
  valueText: string
}

export interface SavedFilterPeekDescription {
  text: string
  steps: string[]
}

export interface SavedFilterPeekProps {
  name: string
  /**
   * What a default tab shows, in words. Only defaults have one.
   */
  description: SavedFilterPeekDescription | null
  /**
   * The default an own tab starts from.
   */
  startsFrom: string | null
  filters: SavedFilterPeekFilter[]
  /**
   * `undefined` when there's no room for another tab.
   */
  onDuplicate?: () => void
  onEdit?: () => void
}

/**
 * What a tab shows, when hovering it. Defaults explain themselves in words;
 * the viewer's own tabs list their filters.
 */
export function SavedFilterPeek({
  name,
  description,
  startsFrom,
  filters,
  onDuplicate,
  onEdit,
}: SavedFilterPeekProps) {
  if (description) {
    return (
      <div className={styles.root} data-test="job-list-saved-filter-peek">
        <div className={styles.header}>
          <span className={styles.name}>{name}</span>
          <span className={styles.origin}>Default</span>
        </div>
        <p className={styles.description}>{description.text}</p>
        {description.steps.length > 0 && (
          <div className={styles.steps}>
            {description.steps.map((step) => (
              <span key={step} className={styles.step}>
                {step}
              </span>
            ))}
          </div>
        )}
        <div className={styles.footer}>
          <span>Can’t be edited</span>
          {onDuplicate && (
            <RawButton
              className={styles.action}
              onPress={onDuplicate}
              testId="job-list-saved-filter-peek-duplicate"
            >
              Duplicate to customize
            </RawButton>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.root} data-test="job-list-saved-filter-peek">
      <div className={styles.header}>
        <span className={styles.name}>{name}</span>
      </div>
      {startsFrom && (
        <div className={styles.filter}>
          <span className={styles.field}>Starts from</span>
          <span className={styles.operator} />
          <span className={styles.value}>{startsFrom}</span>
        </div>
      )}
      {filters.map((filter) => (
        <div key={filter.key} className={styles.filter}>
          <span className={styles.field}>{filter.title}</span>
          <span className={styles.operator}>is</span>
          <span className={styles.value}>{filter.valueText}</span>
        </div>
      ))}
      {!startsFrom && filters.length === 0 && (
        <div className={styles.empty}>Every job</div>
      )}
      {onEdit && (
        <div className={styles.footer}>
          <span />
          <RawButton
            className={styles.action}
            onPress={onEdit}
            testId="job-list-saved-filter-peek-edit"
          >
            Edit tab
          </RawButton>
        </div>
      )}
    </div>
  )
}
